import React from 'react';
import {
  RefreshCw,
  BarChart3,
  Users,
  FileStack,
  Globe,
  TrendingUp,
  ShieldAlert,
  ExternalLink,
} from 'lucide-react';

const PATH_LABELS = {
  '/': 'Home',
  '/about': 'About',
  '/services': 'Services',
  '/industries': 'Industries',
  '/job-seekers': 'Job Seekers',
  '/contact': 'Contact',
};

const ATTACK_LABELS = {
  honeypot: 'Honeypot (bot)',
  recaptcha_fail: 'reCAPTCHA failed',
  rate_limit: 'Rate limit',
};

function pathLabel(path) {
  return PATH_LABELS[path] || path || 'Home';
}

function formatTimestamp(ts) {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    return d.toLocaleString();
  } catch {
    return ts;
  }
}

export default function AdminStatistics({ stats, statsLoading, token, apiUrl }) {
  if (statsLoading) {
    return (
      <div className="statistics-panel">
        <div className="loading-state">
          <RefreshCw size={32} className="spinning" />
          <p>Loading statistics...</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="statistics-panel">
        <div className="empty-state">
          <BarChart3 size={48} />
          <h3>No statistics yet</h3>
          <p>Page views and visitor data will appear here once visitors browse your site.</p>
        </div>
      </div>
    );
  }

  const byCountry = stats.by_country || [];
  const byPath = stats.by_path || [];
  const viewsByDay = stats.views_by_day || [];
  const attacks = stats.attacks || [];
  const seoRanking = stats.seo_ranking || {};
  const maxCountryCount = byCountry.length ? Math.max(...byCountry.map((r) => r.count)) : 1;
  const maxPathCount = byPath.length ? Math.max(...byPath.map((r) => r.count)) : 1;
  const maxDayCount = viewsByDay.length ? Math.max(...viewsByDay.map((d) => d.count), 1) : 1;
  return (
    <div className="statistics-panel">
      <h2 className="statistics-overview-title">Overview</h2>
      <div className="statistics-kpi-grid">
        <div className="statistics-kpi-card statistics-kpi-primary">
          <FileStack size={24} className="statistics-kpi-icon" />
          <span className="statistics-kpi-value">{(stats.total_page_views ?? 0).toLocaleString()}</span>
          <span className="statistics-kpi-label">Total Page Views</span>
        </div>
        <div className="statistics-kpi-card statistics-kpi-primary">
          <Users size={24} className="statistics-kpi-icon" />
          <span className="statistics-kpi-value">{(stats.unique_visitors ?? 0).toLocaleString()}</span>
          <span className="statistics-kpi-label">Unique Visitors</span>
        </div>
        <div className="statistics-kpi-card">
          <TrendingUp size={24} className="statistics-kpi-icon" />
          <span className="statistics-kpi-value">{(stats.page_views_last_24h ?? 0).toLocaleString()}</span>
          <span className="statistics-kpi-label">Last 24 Hours</span>
        </div>
        <div className="statistics-kpi-card">
          <BarChart3 size={24} className="statistics-kpi-icon" />
          <span className="statistics-kpi-value">{(stats.page_views_last_7_days ?? 0).toLocaleString()}</span>
          <span className="statistics-kpi-label">Last 7 Days</span>
        </div>
        <div className="statistics-kpi-card">
          <BarChart3 size={24} className="statistics-kpi-icon" />
          <span className="statistics-kpi-value">{(stats.page_views_last_30_days ?? 0).toLocaleString()}</span>
          <span className="statistics-kpi-label">Last 30 Days</span>
        </div>
      </div>

      {/* Attacks / blocked events */}
      <div className="statistics-table-card statistics-attacks-card">
        <h3 className="statistics-table-title"><ShieldAlert size={20} /> Attacks / Blocked Events</h3>
        {attacks.length > 0 ? (
          <div className="statistics-attacks-list">
            {attacks.map((a) => (
              <div key={a.id || a.timestamp + a.ip} className="statistics-attack-row">
                <span className="statistics-attack-type" title={a.type}>
                  {ATTACK_LABELS[a.type] || a.type}
                </span>
                <code className="statistics-attack-ip">{a.ip || '—'}</code>
                <span className="statistics-attack-detail">{a.detail || a.path || '—'}</span>
                <span className="statistics-attack-time">{formatTimestamp(a.timestamp)}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="statistics-empty">No blocked events yet. Rate limits, honeypot, and reCAPTCHA failures are logged here.</p>
        )}
      </div>

      {/* SEO Ranking */}
      <div className="statistics-table-card statistics-seo-card">
        <h3 className="statistics-table-title"><TrendingUp size={20} /> SEO Ranking</h3>
        <p className="statistics-seo-message">{seoRanking.message || 'Connect Google Search Console for live ranking data.'}</p>
        {seoRanking.link && (
          <a
            href={seoRanking.link}
            target="_blank"
            rel="noopener noreferrer"
            className="statistics-seo-link"
          >
            {seoRanking.link_label || 'Open Google Search Console'}
            <ExternalLink size={16} />
          </a>
        )}
      </div>

      {viewsByDay.length > 0 && (
        <div className="statistics-trend-card">
          <h3 className="statistics-table-title"><TrendingUp size={20} /> Traffic (last 14 days)</h3>
          <div className="statistics-trend-bars">
            {viewsByDay.map((day) => (
              <div key={day.date} className="statistics-trend-bar-wrap" title={`${day.label}: ${day.count} views`}>
                <div
                  className="statistics-trend-bar"
                  style={{ height: `${Math.max(4, (day.count / maxDayCount) * 100)}%` }}
                />
                <span className="statistics-trend-label">{day.label}</span>
                <span className="statistics-trend-value">{day.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="statistics-tables">
        <div className="statistics-table-card">
          <h3 className="statistics-table-title"><Globe size={20} /> Visitors by Country</h3>
          {byCountry.length > 0 ? (
            <div className="statistics-bar-list">
              {byCountry.map((row, i) => (
                <div key={i} className="statistics-bar-row">
                  <span className="statistics-bar-label" title={row.country}>{row.country}</span>
                  <div className="statistics-bar-track">
                    <div
                      className="statistics-bar-fill"
                      style={{ width: `${(row.count / maxCountryCount) * 100}%` }}
                    />
                  </div>
                  <span className="statistics-bar-value">{row.count.toLocaleString()}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="statistics-empty">No country data yet. Country is detected when visitors load the site.</p>
          )}
        </div>
        <div className="statistics-table-card">
          <h3 className="statistics-table-title"><FileStack size={20} /> Top Pages</h3>
          {byPath.length > 0 ? (
            <div className="statistics-bar-list">
              {byPath.map((row, i) => (
                <div key={i} className="statistics-bar-row">
                  <span className="statistics-bar-label" title={row.path}>{pathLabel(row.path)}</span>
                  <div className="statistics-bar-track">
                    <div
                      className="statistics-bar-fill"
                      style={{ width: `${(row.count / maxPathCount) * 100}%` }}
                    />
                  </div>
                  <span className="statistics-bar-value">{row.count.toLocaleString()}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="statistics-empty">No page view data yet. Data is recorded when visitors browse the site.</p>
          )}
        </div>
      </div>
    </div>
  );
}
